import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { MymeType, UUID } from '@myme/utils';
import { useGetTenantCategories } from 'src/hooks/fetch';
import { getInitialChips, MymeChipState, mymeTypesAsChips } from './mymeChipsLogic';

interface SearchContextValue {
  searchField: string;
  chips: MymeChipState[];
  isUserSearching: boolean;
  handleClearSearchValues: () => void;
  handleChangeSearchField: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChip: (chipId: UUID) => void;
  handleRemoveChipsSelection: () => void;
}

export const SearchContext = createContext<SearchContextValue | undefined>(undefined);

interface SearchProviderProps {
  children: ReactNode;
}

export const SearchProvider = ({ children }: SearchProviderProps) => {
  const { pathname } = useLocation();
  const { isCategoriesSuccess, tenantCategories } = useGetTenantCategories();
  const [searchField, setSearchField] = useState('');
  const [chips, setChips] = useState<MymeChipState[]>([]);

  const isUserSearching = searchField.length > 2 || chips.some(chip => chip.isSelected);

  useEffect(() => {
    handleClearSearchValues();
  }, [pathname]);

  useEffect(() => {
    if (isCategoriesSuccess && tenantCategories) {
      setChips(getInitialChips(tenantCategories));
    }
  }, [isCategoriesSuccess]);

  const handleChangeSearchField = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchField(e.target.value);
  };

  const handleRemoveChipsSelection = () =>
    setChips(prevState => prevState.map(chip => ({ ...chip, isSelected: false })));

  const handleClearSearchValues = () => {
    setSearchField('');
    setChips(prevState => prevState.map(chip => ({ ...chip, isSelected: false })));
  };

  const handleSelectChip = (chipId: UUID) => {
    const setNewChipsState = (prevState: MymeChipState[]) =>
      prevState.map(chip =>
        chip.id === chipId
          ? {
              ...chip,
              isSelected: !chip.isSelected
            }
          : chip
      );

    setChips(prevState => setNewChipsState(prevState));
    sessionStorage.setItem('chips', JSON.stringify(setNewChipsState(chips)));
  };

  return (
    <SearchContext.Provider
      value={{
        searchField,
        chips,
        isUserSearching,
        handleClearSearchValues,
        handleChangeSearchField,
        handleSelectChip,
        handleRemoveChipsSelection
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export function useSearchContext() {
  const context = useContext(SearchContext);

  if (context === undefined) {
    throw new Error('You cannot use searchContext without SearchProvider');
  }

  return context;
}

const getSelectedChips = (chips: MymeChipState[]) => chips.filter(chip => chip.isSelected).map(chip => chip.id);

export const getSelectedCategories = (chips: MymeChipState[]) =>
  getSelectedChips(chips).filter(chip => !mymeTypesAsChips.some(mymeType => chip === mymeType.id));

export const getSelectedMymeTypes = (chips: MymeChipState[]): MymeType[] =>
  getSelectedChips(chips).filter(chip => mymeTypesAsChips.some(mymeType => chip === mymeType.id)) as MymeType[];
